import React, { useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './app.css';
import { api, ApiError, setRefresh, setToken } from './lib/api';
import { useResource, useSession, useToast } from './lib/hooks';
import { workflowColour, today } from './lib/format';
import type { Building, Catalogue, Employee, Locations, Property, Rule, Session, Task } from './lib/types';
import { Today } from './screens/Today';
import { Dashboard } from './screens/Dashboard';
import { Tasks, NewTask, type NewTaskPreset } from './screens/Tasks';
import { TaskDrawer } from './screens/TaskDrawer';
import { CompleteDrawer } from './screens/CompleteDrawer';
import { Properties } from './screens/Properties';
import { PortfolioMap } from './screens/PortfolioMap';
import { Security } from './screens/Security';
import { Leases } from './screens/Leases';
import { Tenants } from './screens/Tenants';
import { Finance } from './screens/Finance';
import { Team } from './screens/Team';
import { Timeline } from './screens/Timeline';
import { Projections } from './screens/Projections';
import { WorkflowMap } from './screens/WorkflowMap';
import { BillingScreen, Standing, Suspended } from './screens/Billing';

/**
 * BurjManage — Operations Console.
 *
 * One codebase for the desk and the phone. A manager on a phone is asking a
 * different question — "is anything going wrong and can I unblock it from here?"
 * — so Today leads on decisions, and the tab bar drops to five items below 760px.
 * The alternative was two applications for one product, which is a permanent
 * cost paid before knowing whether it is needed.
 *
 * **This application contains no workflow rules.** Every question, every
 * resolution and what each one creates is fetched from GET /v1/workflows. That
 * is what lets a rule change on the server reach this console with no release,
 * and `npm run check:rules` fails the build if any rule name appears here.
 */

/** The API's own cap on GET /v1/tasks. */
const TASK_LIMIT = 500;

export type Screen =
  | 'today' | 'dashboard' | 'tasks' | 'properties' | 'map' | 'security' | 'leases' | 'tenants'
  | 'finance' | 'team' | 'timeline' | 'projections' | 'workflows' | 'billing';

/**
 * Screens that show the company's money or its book of tenancies. The API
 * refuses their reads to a specialist — leases and tenants included, since they
 * carry rents, deposits and phone numbers — and so does the menu.
 */
const MONEY: ReadonlySet<Screen> = new Set(['dashboard', 'finance', 'projections', 'leases', 'tenants', 'billing']);

const DESKTOP_TABS: Array<[Screen, string]> = [
  ['today', 'Today'], ['dashboard', 'Dashboard'], ['tasks', 'Tasks'], ['timeline', 'Timeline'],
  ['properties', 'Properties'], ['map', 'Map'], ['security', 'Security'],
  ['leases', 'Leases'], ['tenants', 'Tenants'], ['finance', 'Finance'],
  ['team', 'Team'], ['projections', 'Projections'], ['workflows', 'Workflow map'], ['billing', 'Billing'],
];
/** Five is the ceiling on a phone tab bar. The rest stay reachable from Today. */
const MOBILE_TABS: Array<[Screen, string]> = [
  ['today', 'Today'], ['tasks', 'Tasks'], ['map', 'Map'], ['finance', 'Money'], ['team', 'Team'],
];

export interface Ctx {
  catalogue: Catalogue;
  ruleFor: (name: string) => Rule | null;
  properties: Property[];
  /** Towers and standalone buildings: the names and pins their properties share. */
  buildings: Building[];
  /** Places of operation (countries, cities, districts), from the admin console. */
  locations: Locations | null;
  employees: Employee[];
  propertyName: (id: string) => string;
  employeeName: (id: string | null) => string | undefined;
  go: (s: Screen) => void;
  /** Opens a task: its details, its history, and the edit form. */
  open: (t: Task) => void;
  /** Goes straight to completing it. */
  complete: (t: Task) => void;
  newTask: (preset?: NewTaskPreset) => void;
  toast: (title: string, detail?: string) => void;
  refresh: () => Promise<void>;
  me: Session['staff'];
  /** Manager or admin: finance, rent cycle, deleting tasks, editing tenants and rooms. */
  canManage: boolean;
  isAdmin: boolean;
  /** A read-only support session: everything visible, nothing changeable. */
  readOnly: boolean;
}

function App({ emailed, startOn }: { emailed: EmailedLink | null; startOn: Screen }): React.JSX.Element {
  const [session, setSession] = useSession<Session>('burj.ops');
  const [link, setLink] = useState<EmailedLink | null>(emailed);
  const [screen, setScreen] = useState<Screen>(startOn);
  const [opened, setOpened] = useState<Task | null>(null);
  const [completing, setCompleting] = useState<Task | null>(null);
  const [creating, setCreating] = useState<NewTaskPreset | null>(null);
  const [toast, showToast] = useToast();

  setToken(session?.accessToken ?? null);
  setRefresh(session?.refreshToken ?? null,
    (fresh) => setSession(session ? { ...session, ...fresh } : null));
  const signOut = (): void => {
    if (session?.refreshToken) void api.logout(session.refreshToken);
    setSession(null);
  };

  // Who is signed in, not which token they hold: a refreshed token every fifteen
  // minutes must not reload every screen and close whatever drawer is open.
  const who = session ? `${session.staff.id}:${session.support ? 'support' : 'own'}` : null;

  useEffect(() => {
    const out = (): void => setSession(null);
    window.addEventListener('burj:signed-out', out);
    return () => window.removeEventListener('burj:signed-out', out);
  }, [setSession]);

  const catalogue = useResource(
    () => (session ? api.workflows() : Promise.resolve(null)), [who]);
  const tasks = useResource(
    () => (session ? api.tasks({ status: 'open', limit: TASK_LIMIT }) : Promise.resolve(null)), [who]);
  const properties = useResource(
    () => (session ? api.properties() : Promise.resolve(null)), [who]);
  const buildings = useResource(
    () => (session ? api.buildings() : Promise.resolve(null)), [who]);
  // the same for everyone and rarely changed: loaded once a session, and a failure is not fatal
  const locations = useResource(
    () => (session ? api.locations().catch(() => null) : Promise.resolve(null)), [who]);
  // what the company owes BurjManage: a banner on every screen while something is due
  const billing = useResource(
    () => (session && session.staff.role !== 'specialist' ? api.billing().catch(() => null) : Promise.resolve(null)),
    [who]);
  const employees = useResource(
    () => (session ? api.employees().catch(() => ({ employees: [] })) : Promise.resolve(null)),
    [who]);

  const refresh = useCallback(async () => {
    await Promise.all([tasks.reload(), properties.reload(), buildings.reload(), employees.reload(), billing.reload()]);
  }, [tasks, properties, buildings, employees, billing]);

  if (!session && link) {
    return <FromEmail link={link} onSignedIn={(s) => { setLink(null); setSession(s); }} onDone={() => setLink(null)} />;
  }
  if (!session) return <SignIn onSignedIn={setSession} />;
  // suspended for an unpaid invoice: show what is owed and how to pay, not an error
  if (catalogue.error && (catalogue.error as ApiError).code === 'org_suspended') {
    return <Suspended canSee={session.staff.role !== 'specialist'} onSignOut={signOut} />;
  }
  if (catalogue.error) return <Fatal error={catalogue.error} onSignOut={signOut} />;
  // The render straight after signing in still holds the signed-out result: the
  // reload starts in an effect, after this render. Without the !data test the
  // console read the catalogue of nobody and went blank on every sign-in.
  if (catalogue.loading || tasks.loading || !catalogue.data) {
    return <div className="gate"><div className="spinner" /></div>;
  }

  const cat = catalogue.data!;
  const openTasks = tasks.data?.tasks ?? [];
  const props = properties.data?.properties ?? [];
  const staff = employees.data?.employees ?? [];
  const canManage = session.staff.role !== 'specialist';

  const go = (s: Screen): void => setScreen(!canManage && MONEY.has(s) ? 'today' : s);

  const ctx: Ctx = {
    catalogue: cat,
    ruleFor: (name) => cat.rules.find((r) => r.name === name) ?? null,
    properties: props,
    buildings: buildings.data?.buildings ?? [],
    locations: locations.data ?? null,
    employees: staff,
    propertyName: (id) => props.find((p) => p.id === id)?.name ?? '—',
    employeeName: (id) => (id ? staff.find((e) => e.id === id)?.name : undefined),
    go,
    open: setOpened,
    complete: (t) => { setOpened(null); setCompleting(t); },
    newTask: (preset) => setCreating(preset ?? {}),
    toast: showToast,
    refresh,
    me: session.staff,
    canManage,
    isAdmin: session.staff.role === 'admin',
    readOnly: session.support?.readOnly ?? false,
  };

  // the API caps the list and does not say whether more exist; a full page means
  // the latest-due tasks may be missing, and every count below is a floor
  const truncated = openTasks.length >= TASK_LIMIT;
  const overdue = openTasks.filter((t) => t.dueDate < today()).length;
  // every control that changes something carries class "w"; a read-only support
  // session hides them all at once rather than letting each one fail on click
  document.body.classList.toggle('readonly', ctx.readOnly);
  const visible = (key: Screen): boolean => canManage || !MONEY.has(key);
  const current = visible(screen) ? screen : 'today';

  const body = {
    today: <Today ctx={ctx} tasks={openTasks} properties={props} />,
    dashboard: <Dashboard ctx={ctx} tasks={openTasks} properties={props} />,
    tasks: <Tasks ctx={ctx} tasks={openTasks} properties={props} />,
    timeline: <Timeline ctx={ctx} tasks={openTasks} />,
    properties: <Properties ctx={ctx} />,
    map: <PortfolioMap ctx={ctx} tasks={openTasks} />,
    security: <Security ctx={ctx} />,
    leases: <Leases ctx={ctx} />,
    tenants: <Tenants ctx={ctx} />,
    finance: <Finance ctx={ctx} tasks={openTasks} />,
    team: <Team ctx={ctx} tasks={openTasks} employees={staff} />,
    projections: <Projections ctx={ctx} />,
    workflows: <WorkflowMap catalogue={cat} />,
    billing: <BillingScreen ctx={ctx} />,
  }[current];

  return (
    <>
      <header className="top">
        <span className="wm">Burj<em>Manage</em></span>
        <span className="pill">Operations</span>
        <div className="r">
          <span className="hideSm">{session.staff.name} · {session.staff.role}</span>
          <button className="btn sm" onClick={signOut}>{session.support ? 'End session' : 'Sign out'}</button>
        </div>
      </header>

      {session.support ? (
        <div className="banner support">
          <b>Support session{session.support.readOnly ? ' — read-only' : ''}.</b> Opened from the admin
          console, recorded in the platform audit log, and ends at{' '}
          {new Date(session.support.expiresAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}.
          {session.support.readOnly ? ' Nothing here can be changed.' : ''}
        </div>
      ) : null}

      {billing.data && current !== 'billing' && <Standing billing={billing.data} onOpen={() => go('billing')} />}

      {truncated && (
        <div className="banner support">
          <b>Showing the first {TASK_LIMIT} open tasks.</b> More exist; the latest-due are not listed
          and the counts here are lower than the real ones.
        </div>
      )}

      <nav className="tabs noMobile">
        {DESKTOP_TABS.filter(([key]) => visible(key)).map(([key, label]) => (
          <button key={key} className={current === key ? 'on' : ''} onClick={() => go(key)}>
            {label}
            {key === 'tasks' && overdue > 0 && <span className="tag t-bad">{overdue}</span>}
          </button>
        ))}
      </nav>

      <main className="wrap">{body}</main>

      <nav className="tabbar">
        {MOBILE_TABS.filter(([key]) => visible(key)).map(([key, label]) => (
          <button key={key}
            className={current === key || (key === 'today' && !MOBILE_TABS.some(([k]) => k === current)) ? 'on' : ''}
            onClick={() => go(key)}>{label}</button>
        ))}
      </nav>

      {opened && !completing && (
        <TaskDrawer ctx={ctx} task={opened} onClose={() => setOpened(null)}
          onChanged={async (msg) => { setOpened(null); await refresh(); if (msg) showToast(msg); }} />
      )}

      {completing && (
        <CompleteDrawer
          ctx={ctx}
          task={completing}
          onClose={() => setCompleting(null)}
          onDone={async (result) => {
            setCompleting(null);
            await refresh();
            result.createdTasks.length
              ? showToast(`${result.createdTasks.length} task${result.createdTasks.length > 1 ? 's' : ''} created`,
                  result.createdTasks.map((t) => t.name).join(' · '))
              : showToast('Task closed', result.chainClosed ? 'The chain ended here.' : 'Recorded.');
          }}
        />
      )}

      {creating && (
        <NewTask ctx={ctx} preset={creating} onClose={() => setCreating(null)}
          onCreated={async (task, title) => {
            setCreating(null);
            await refresh();
            showToast(title, `${task.name} · ${ctx.propertyName(task.propertyId)}`);
          }} />
      )}

      {toast && <div className="toast"><b>{toast.title}</b>{toast.detail && <span>{toast.detail}</span>}</div>}
    </>
  );
}

/* ------------------------------------------------------------------ gate */

function SignIn({ onSignedIn }: { onSignedIn: (s: Session) => void }): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true); setMessage(null);
    try {
      onSignedIn(await api.login(email, password));
    } catch (err) {
      setMessage(err instanceof ApiError && err.code === 'offline'
        ? 'Cannot reach the API. Is the platform running?'
        : (err as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <div className="gate"><div className="gatebox">
      <div style={{ textAlign: 'center' }}>
        <div className="wm" style={{ color: 'var(--ink)', fontSize: 22 }}>Burj<em>Manage</em></div>
        <div className="sub" style={{ marginTop: 4 }}>Operations console</div>
      </div>
      <form className="gatecard" onSubmit={submit}>
        {message && <div className="note bad" style={{ marginBottom: 14 }}>{message}</div>}
        <div className="field">
          <label htmlFor="email">Work email</label>
          <input id="email" type="email" autoComplete="username" value={email}
            onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input id="password" type="password" autoComplete="current-password" value={password}
            onChange={(e) => setPassword(e.target.value)} required />
        </div>
        <button className="btn accent full" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
        <button type="button" className="btn quiet full" style={{ marginTop: 8 }} disabled={!email}
          onClick={() => void api.forgot(email).then(
            () => setMessage('If that address has an account, a reset link is on its way.'),
            (err: Error) => setMessage(err.message))}>
          Forgot your password?
        </button>
      </form>
    </div></div>
  );
}

/** A link from an email: an invitation to join, or a password reset. */
type EmailedLink = { kind: 'invite' | 'reset'; token: string };

/**
 * Where the links in the invitation and reset emails land. Without this page
 * both emails pointed at a console that ignored them: an invited colleague, or
 * a new customer's first admin, had no way to set a password at all.
 */
function FromEmail({ link, onSignedIn, onDone }: {
  link: EmailedLink; onSignedIn: (s: Session) => void; onDone: () => void;
}): React.JSX.Element {
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [message, setMessage] = useState<{ tone: 'bad' | 'ok'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);
  const invite = link.kind === 'invite';
  const valid = password.length >= 12 && password === again;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true); setMessage(null);
    try {
      if (invite) {
        onSignedIn(await api.acceptInvite(link.token, password));
      } else {
        await api.resetPassword(link.token, password);
        setChanged(true);
      }
    } catch (err) {
      const code = err instanceof ApiError ? err.code : undefined;
      setMessage({ tone: 'bad', text:
        code === 'invitation_invalid' ? 'This invitation has expired or was already used. Ask whoever invited you to send another.'
        : code === 'reset_invalid' ? 'This link has expired or was already used. Ask for another from the sign-in page.'
        : (err as Error).message });
    } finally { setBusy(false); }
  };

  return (
    <div className="gate"><div className="gatebox">
      <div style={{ textAlign: 'center' }}>
        <div className="wm" style={{ color: 'var(--ink)', fontSize: 22 }}>Burj<em>Manage</em></div>
        <div className="sub" style={{ marginTop: 4 }}>{invite ? 'Join your team' : 'Choose a new password'}</div>
      </div>
      {changed ? (
        <div className="gatecard">
          <div className="note ok" style={{ marginBottom: 14 }}>
            Your password is changed, and every other place you were signed in has been signed out.
          </div>
          <button className="btn accent full" onClick={onDone}>Sign in</button>
        </div>
      ) : (
        <form className="gatecard" onSubmit={submit}>
          {message && <div className={`note ${message.tone}`} style={{ marginBottom: 14 }}>{message.text}</div>}
          <p className="sub" style={{ marginTop: 0 }}>
            {invite
              ? 'Choose a password of at least twelve characters. You will be signed in straight away.'
              : 'At least twelve characters. Nobody at your company sees it, and nobody can set it for you.'}
          </p>
          <div className="field">
            <label htmlFor="pw1">Password</label>
            <input id="pw1" type="password" autoComplete="new-password" value={password}
              onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="pw2">The same again</label>
            <input id="pw2" type="password" autoComplete="new-password" value={again}
              onChange={(e) => setAgain(e.target.value)} required />
            {again && password !== again && <div className="hint">These do not match yet.</div>}
          </div>
          <button className="btn accent full" disabled={!valid || busy}>
            {busy ? 'Saving…' : invite ? 'Set password and sign in' : 'Change password'}
          </button>
          <button type="button" className="btn quiet full" style={{ marginTop: 8 }} onClick={onDone}>
            I already have a password
          </button>
        </form>
      )}
    </div></div>
  );
}

const Fatal = ({ error, onSignOut }: { error: Error; onSignOut: () => void }): React.JSX.Element => (
  <div className="gate"><div className="gatebox">
    <div className="note bad"><b>Could not load.</b><br />{error.message}</div>
    <button className="btn full" style={{ marginTop: 12 }} onClick={onSignOut}>Sign out</button>
  </div></div>
);

/* -------------------------------------------------------- shared pieces */

export const Stat = ({ label, value, foot, tone }: {
  label: string; value: string | number; foot?: string; tone?: 'hot' | 'good';
}): React.JSX.Element => (
  <div className="stat">
    <div className="lab">{label}</div>
    <div className={`val ${tone ?? ''}`}>{value}</div>
    {foot && <div className="foot">{foot}</div>}
  </div>
);

export const Loading = (): React.JSX.Element => <div className="spinner" />;

export const Failed = ({ error, onRetry }: { error: Error; onRetry: () => void }): React.JSX.Element => (
  <div className="note bad">
    <b>Could not load.</b> {error.message}
    <div style={{ marginTop: 10 }}><button className="btn sm" onClick={onRetry}>Try again</button></div>
  </div>
);

export function Drawer({ title, subtitle, children, footer, onClose }: {
  title: string; subtitle?: string; children: React.ReactNode;
  footer?: React.ReactNode; onClose: () => void;
}): React.JSX.Element {
  useEffect(() => {
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="dh">
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 18 }}>{title}</h2>
            {subtitle && <div className="sub" style={{ marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button className="btn sm quiet" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="db">{children}</div>
        {footer && <div className="df">{footer}</div>}
      </aside>
    </>
  );
}

/** Asks once, in the page, rather than through window.confirm — which some embedded browsers block. */
export function Confirm({ title, detail, action, danger, onConfirm, onClose }: {
  title: string; detail: string; action: string; danger?: boolean;
  onConfirm: () => Promise<void>; onClose: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Drawer title={title} onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className={`btn ${danger ? 'danger' : 'accent'}`} disabled={busy}
          onClick={async () => {
            setBusy(true); setError(null);
            try { await onConfirm(); } catch (e) { setError((e as Error).message); setBusy(false); }
          }}>{busy ? 'Working…' : action}</button>
      </>}>
      {error && <div className="note bad" style={{ marginBottom: 14 }}>{error}</div>}
      <div className={`note ${danger ? 'warn' : ''}`}>{detail}</div>
    </Drawer>
  );
}

const daysLate = (due: string): number => Math.round((Date.parse(today()) - Date.parse(due)) / 864e5);

/** One row per task. The same component on the desk and the phone. */
export function TaskRow({ task, ctx, badge }: {
  task: Task; ctx: Ctx; badge?: React.ReactNode;
}): React.JSX.Element {
  const late = task.dueDate < today();
  return (
    <button className="rowcard" onClick={() => ctx.open(task)}>
      <span className="barc" style={{ background: workflowColour(task.workflow) }} />
      <span className="body">
        <span className="tt">
          {task.name}
          {task.severity === 'Critical' && <span className="tag t-sos" style={{ marginLeft: 7 }}>SOS</span>}
          {task.severity === 'High' && <span className="tag t-bad" style={{ marginLeft: 7 }}>High</span>}
        </span>
        <span className="sub" style={{ display: 'block' }}>
          {task.propertyName ?? ctx.propertyName(task.propertyId)}{task.description ? ` · ${task.description}` : ''}
        </span>
        <span className="meta">
          <span className={`tag t-${task.type}`}>{task.type}</span>
          {task.status === 'InProgress' && <span className="tag t-InProgress">In progress</span>}
          {late
            ? <span className="tag t-bad">{daysLate(task.dueDate)}d late</span>
            : <span className="tag t-mute">{task.dueDate}</span>}
          {ctx.employeeName(task.assigneeId) && (
            <span className="tag t-info">{ctx.employeeName(task.assigneeId)!.split(' ')[0]}</span>
          )}
          {badge}
          {task.amount > 0 && <span className="amt">{task.currency} {task.amount.toLocaleString('en-AE')}</span>}
        </span>
      </span>
    </button>
  );
}

/**
 * A support session handed over by the admin console, as `#token=…`.
 *
 * Read once, before anything renders, and then scrubbed from the address bar
 * and the history — a token must not linger where a screenshot or a shared link
 * would carry it. The claims are read only to label the session; the API
 * verifies the token on every request and refuses writes in a read-only grant.
 * It lives in this tab's sessionStorage and nowhere else.
 */
export function adoptSupportSession(hash: string): Session | null {
  const m = /[#&]token=([^&]+)/.exec(hash);
  if (!m) return null;
  try {
    const token = decodeURIComponent(m[1]!);
    const body = token.split('.')[1] ?? '';
    const claims = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/'))) as {
      typ?: string; sub?: string; imp?: string; ro?: boolean; exp?: number;
    };
    if (claims.typ !== 'platform' || !claims.imp || !claims.sub) return null;
    return {
      accessToken: token,
      expiresIn: Math.max(0, (claims.exp ?? 0) - Math.floor(Date.now() / 1000)),
      // a manager's view: everything visible, finance included
      staff: { id: claims.sub, name: 'BurjManage support', role: 'manager', orgId: claims.imp },
      support: { readOnly: claims.ro !== false, expiresAt: (claims.exp ?? 0) * 1000 },
    };
  } catch {
    return null;
  }
}

const handoff = adoptSupportSession(window.location.hash);
if (window.location.hash.includes('token=')) {
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}
if (handoff) {
  try { sessionStorage.setItem('burj.ops', JSON.stringify(handoff)); } catch { /* private mode: the session simply does not persist */ }
}

// /invite?token=… and /reset?token=… — read once, then taken out of the address
// bar and the history, as the support hand-off is
const linkToken = new URLSearchParams(window.location.search).get('token');
const linkPath = window.location.pathname.replace(/\/+$/, '');
const emailed: EmailedLink | null = linkToken && (linkPath === '/invite' || linkPath === '/reset')
  ? { kind: linkPath === '/invite' ? 'invite' : 'reset', token: linkToken } : null;
if (emailed) window.history.replaceState(null, '', '/');
// /billing, from an invoice or reminder email: open on the bill once signed in
const startOn: Screen = linkPath === '/billing' ? 'billing' : 'today';
if (startOn === 'billing') window.history.replaceState(null, '', '/');

createRoot(document.getElementById('root')!).render(<React.StrictMode><App emailed={emailed} startOn={startOn} /></React.StrictMode>);
